import { type FC, useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Stack,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import type { ObjectItem, SelectObjectTypeConfig } from "../types";
import { SelectObjectListItem } from "./SelectObjectListItem";

interface SelectObjectDialogProps {
  config: SelectObjectTypeConfig;
  onSelectObject: (item: ObjectItem) => void;
  onClose: () => void;
}

export const SelectObjectDialog: FC<SelectObjectDialogProps> = ({
  config,
  onSelectObject,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { selectTitle, queryList, getList, getListVariables } = config;
  const [search, setSearch] = useState("");

  const { data, loading } = useQuery(queryList, {
    variables: getListVariables
      ? getListVariables(search)
      : { page: 0, rowsPerPage: 50, filter: { search } },
  });

  const nodes: ObjectItem[] = data ? getList(data).nodes : [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleConfirm = () => {
    const item = nodes.find((node) => node.id === selectedId);
    if (item) {
      onSelectObject(item);
    }
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open
    >
      <DialogHeader
        title={
          selectTitle ??
          t("shared.selectObject.dialogTitle", {
            defaultValue: "Выберите объект",
          })
        }
        actions={
          <Button
            disableElevation
            autoFocus
            variant="contained"
            color="primary"
            disabled={!selectedId}
            onClick={handleConfirm}
          >
            {t("shared.selectObject.select", { defaultValue: "Выбрать" })}
          </Button>
        }
        loading={loading}
        onClose={onClose}
      >
        <TextField
          label={t("shared.selectObject.search", { defaultValue: "Поиск" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
      </DialogHeader>
      <DialogContent sx={{ p: 1 }}>
        <Box sx={{ minHeight: "400px" }}>
          {nodes.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              {t("shared.selectObject.noItems", {
                defaultValue: "Нет объектов",
              })}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {nodes.map((item) => (
                <SelectObjectListItem
                  key={item.id}
                  item={item}
                  config={config}
                  selected={selectedId === item.id}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
